import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  BarChart3, TrendingUp, TrendingDown, Lightbulb, Target, RefreshCw, Download,
} from "lucide-react";

/**
 * YTD Report Panel — full-year financial + activity dashboard with
 * rule-based (no LLM) auto-suggestions from the backend.
 * Shown inside MyProfile under the GPS section.
 */
export default function GPSYtdReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/gps/ytd-report")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const r = await api.get("/gps/ytd-report/pdf", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const now = new Date();
      a.download = `GPS_YTD_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descarregado.");
    } catch (err) { toast.error(formatApiError(err)); }
    setPdfLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (!data) {
    return (
      <div className="kw-card p-6" data-testid="ytd-report-loading">
        <div className="kw-overline">A carregar relatório YTD…</div>
      </div>
    );
  }

  const plan = data.plan || {};
  const ytd = data.ytd || {};
  const activity = data.activity || {};
  const eur = (n) => `${Math.round(Number(n) || 0).toLocaleString("pt-PT")} €`;
  const isAhead = (ytd.delta_eur || 0) >= 0;
  const pct = Math.max(0, ytd.pct || 0);
  const barColor = pct >= 100 ? "#22C55E" : pct >= 85 ? "#EAB308" : "#E50000";

  if (!plan.is_configured) {
    return (
      <div className="kw-card p-6" data-testid="ytd-report-empty">
        <div className="kw-overline flex items-center gap-2">
          <BarChart3 className="w-3 h-3 text-[#22C55E]" /> Relatório YTD
        </div>
        <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mt-1">
          Ainda sem <span className="text-[#22C55E]">dados</span>
        </h2>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl leading-snug">
          Preenche o teu 1-3-5 com o Grande Objetivo (€), o ticket médio e as três conversões
          para desbloqueares o relatório mensal com sugestões automáticas.
        </p>
        {data.suggestions?.length > 0 && (
          <div className="mt-4 space-y-2">
            {data.suggestions.map((s, i) => (
              <div key={i} className="text-xs text-[#EAB308] flex items-start gap-2">
                <Lightbulb className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {s}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div id="ytd-report" className="kw-card p-0 overflow-hidden scroll-mt-20" data-testid="ytd-report">
      {/* Header */}
      <div className="p-5 border-b border-[#222] flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="kw-overline flex items-center gap-2">
            <BarChart3 className="w-3 h-3 text-[#22C55E]" /> Relatório Mensal · YTD
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mt-1">
            Faturação <span className="text-[#22C55E]">YTD</span>
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={load}
            disabled={loading}
            className="kw-btn-secondary text-xs inline-flex items-center gap-2"
            data-testid="ytd-report-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </button>
          <button
            onClick={downloadPdf}
            disabled={pdfLoading}
            className="kw-btn-primary text-xs inline-flex items-center gap-2"
            data-testid="ytd-report-pdf"
          >
            <Download className="w-3 h-3" /> {pdfLoading ? "A gerar…" : "PDF"}
          </button>
        </div>
      </div>

      {/* YTD summary */}
      <div className="p-5 grid md:grid-cols-4 gap-0 border-b border-[#222]">
        <YtdKPI label="Target YTD" value={eur(ytd.target_eur || 0)} color="#3B82F6" />
        <YtdKPI label="Real YTD" value={eur(ytd.actual_eur || 0)} color="#EAB308" />
        <YtdKPI
          label="Delta"
          value={(isAhead ? "+" : "") + eur(ytd.delta_eur || 0)}
          color={isAhead ? "#22C55E" : "#E50000"}
          icon={isAhead ? TrendingUp : TrendingDown}
        />
        <YtdKPI label="% Cumprido" value={`${pct.toFixed(0)}%`} color={barColor} />
      </div>

      {/* Progress bar */}
      <div className="px-5 pt-4">
        <div className="h-3 bg-[#141414] relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 transition-all" style={{ width: `${Math.min(100, pct)}%`, background: barColor }} />
          {pct > 100 && (
            <div className="absolute inset-y-0 right-0" style={{ width: `${Math.min(50, pct - 100)}%`, background: "#22C55E", opacity: 0.5 }} />
          )}
        </div>
        <div className="text-[10px] text-[#737373] mt-1 font-mono">Semanas decorridas YTD: {ytd.weeks_elapsed}</div>
      </div>

      {/* Monthly breakdown */}
      <div className="p-5">
        <div className="kw-overline mb-3">Mês a Mês</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ytd-report-monthly">
            <thead>
              <tr className="border-b border-[#222]">
                <th className="text-left text-[10px] uppercase tracking-widest text-[#737373] font-bold py-2 pr-2">Mês</th>
                <th className="text-right text-[10px] uppercase tracking-widest text-[#737373] font-bold py-2 px-2">Target</th>
                <th className="text-right text-[10px] uppercase tracking-widest text-[#737373] font-bold py-2 px-2">Real</th>
                <th className="text-right text-[10px] uppercase tracking-widest text-[#737373] font-bold py-2 px-2">Δ</th>
                <th className="text-right text-[10px] uppercase tracking-widest text-[#737373] font-bold py-2 pl-2">%</th>
              </tr>
            </thead>
            <tbody>
              {(data.monthly || []).map((m, i) => {
                const bg = m.is_past_or_current ? "" : "opacity-40";
                const dColor = m.actual_eur > 0 ? (m.delta_eur >= 0 ? "text-[#22C55E]" : "text-[#E50000]") : "text-[#555]";
                return (
                  <tr key={i} className={`border-b border-[#1A1A1A] ${bg}`} data-testid={`ytd-report-row-${i}`}>
                    <td className="py-2 pr-2 font-bold uppercase text-xs">{m.month_label.slice(0, 3)}</td>
                    <td className="py-2 px-2 text-right font-mono text-xs text-[#A3A3A3]">{eur(m.target_eur)}</td>
                    <td className="py-2 px-2 text-right font-mono text-xs">{m.actual_eur > 0 ? eur(m.actual_eur) : <span className="text-[#555]">—</span>}</td>
                    <td className={`py-2 px-2 text-right font-mono text-xs font-bold ${dColor}`}>
                      {m.actual_eur > 0 ? (m.delta_eur >= 0 ? "+" : "") + eur(m.delta_eur) : "—"}
                    </td>
                    <td className={`py-2 pl-2 text-right font-mono text-xs font-bold ${m.pct >= 100 ? "text-[#22C55E]" : m.pct >= 85 ? "text-[#EAB308]" : m.actual_eur > 0 ? "text-[#E50000]" : "text-[#555]"}`}>
                      {m.actual_eur > 0 ? `${m.pct.toFixed(0)}%` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Activity vs Plan */}
      <div className="p-5 border-t border-[#222]" data-testid="ytd-report-activity">
        <div className="kw-overline mb-3 flex items-center gap-2">
          <Target className="w-3 h-3 text-[#EAB308]" /> Atividade YTD vs Plano
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <ActivityCell label="Contactos" real={activity.contactos_ytd} expected={activity.expected_contactos_ytd} />
          <ActivityCell label="Reuniões" real={activity.reunioes_ytd} expected={activity.expected_reunioes_ytd} />
          <ActivityCell label="Angariações" real={activity.angariacoes_ytd} expected={activity.expected_angariacoes_ytd} />
          <ActivityCell label="Vendas" real={activity.vendas_ytd} expected={activity.expected_vendas_ytd} />
        </div>
      </div>

      {/* Suggestions */}
      {data.suggestions?.length > 0 && (
        <div className="p-5 border-t border-[#222] bg-[#0F0F0F]" data-testid="ytd-report-suggestions">
          <div className="kw-overline mb-3 flex items-center gap-2 text-[#EAB308]">
            <Lightbulb className="w-3 h-3" /> Sugestões automáticas para o próximo mês
          </div>
          <ul className="space-y-2">
            {data.suggestions.map((s, i) => (
              <li key={i} className="text-sm text-[#E5E5E5] leading-relaxed flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#EAB308] flex-shrink-0 mt-2" />
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function YtdKPI({ label, value, color = "#E50000", icon: Icon }) {
  return (
    <div className="p-4 border-r border-[#222] last:border-r-0">
      <div className="kw-overline text-[10px]">{label}</div>
      <div className="flex items-center gap-2 mt-1">
        {Icon && <Icon className="w-4 h-4" style={{ color }} />}
        <div className="font-display text-2xl font-black" style={{ color }}>{value}</div>
      </div>
    </div>
  );
}

function ActivityCell({ label, real, expected }) {
  const pct = expected > 0 ? Math.round((100 * (real || 0)) / expected) : 0;
  const color = pct >= 90 ? "#22C55E" : pct >= 70 ? "#EAB308" : "#E50000";
  return (
    <div className="border border-[#333] p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</div>
      <div className="flex items-baseline gap-1 mt-1">
        <div className="font-display text-2xl font-black">{real || 0}</div>
        <div className="text-xs text-[#737373]">/ {expected || 0}</div>
      </div>
      <div className="text-[10px] font-mono mt-1" style={{ color }}>{pct}% do esperado</div>
    </div>
  );
}
